import { useLayoutEffect } from 'react';
import { shouldResetScrollOnRoute, type RouteNavigationType } from '../lib/routeScroll';
import { getScrollMemory } from '../lib/scrollRestore';

/**
 * The pathname the previous page settled on. Module scope on purpose: this
 * component is mounted fresh for every page (it sits inside the keyed
 * transition wrapper in App.tsx's AnimatedRoutes), and the public-info pages
 * render under a different provider tree than the Hub, so any per-instance
 * ref would forget the last page at exactly the moment it is needed. It also
 * makes a re-mount on the SAME path (StrictMode's second pass, a loading
 * screen that unmounts and remounts the routes) a no-op instead of a jump.
 */
let lastSettledPathname: string | null = null;

interface RouteScrollResetProps {
  pathname: string;
  hash: string;
  state: unknown;
  navigationType: RouteNavigationType;
  /** react-router's location.key: which history entry this page is. */
  locationKey: string;
}

/**
 * Starts the page that just opened at the top (Packet CX-4). The rule and the
 * reason are in lib/routeScroll.ts.
 *
 * WHERE IT SITS, AND WHY THAT MATTERS. AnimatedRoutes keys one transition
 * wrapper by pathname and runs AnimatePresence in mode="wait", so the old page
 * fades out first and the new one mounts afterwards. This component is the
 * first child of that new wrapper and does its work in a LAYOUT effect:
 *
 *   - It runs when the new page's wrapper mounts, not when the URL changes.
 *     Resetting at the URL change would throw the OLD page to its own top for
 *     the 220ms it is still fading out, a flash of the wrong half of the
 *     page. Doing it as the new page mounts shows the old page undisturbed
 *     and the new one at its top.
 *   - A layout effect runs before every passive effect in the same commit, and
 *     this component is a sibling placed BEFORE the page, so the reset is
 *     always behind the page's own scroll-to-a-post, scroll-to-a-reply and
 *     scroll-to-a-section effects, never on top of them. A page that scrolls
 *     itself after it loads therefore wins, which is the point: those pages
 *     were told where to go by a hash or a ?focusId=, and this must not undo
 *     it.
 *
 * THE LOCATION COMES IN AS PROPS, NOT FROM useLocation(). AnimatePresence keeps
 * rendering the OLD wrapper element, with its old props, while it fades out,
 * but a hook would read the router's live location and re-run this effect for
 * the exiting page the instant the URL changed, which is the early reset the
 * paragraph above rules out. Props stay frozen with the element.
 *
 * BACK AND FORWARD ARE NOT LEFT TO THE BROWSER ALONE. A POP to a different page
 * asks lib/scrollRestore.ts to put the offset that page was left at back once
 * the document is tall enough (the reset above leaves the window at 0 on the
 * page being returned from, which defeats the browser's own retry). It is
 * started here, as the page mounts, for the same ordering reason as the reset,
 * and is mutually exclusive with it: a POP never resets.
 *
 * Renders nothing.
 */
export default function RouteScrollReset({ pathname, hash, state, navigationType, locationKey }: RouteScrollResetProps) {
  useLayoutEffect(() => {
    const memory = getScrollMemory();
    memory?.restoreOnPop({
      key: locationKey,
      fromPathname: lastSettledPathname,
      toPathname: pathname,
      navigationType,
    });
    const reset = shouldResetScrollOnRoute({
      fromPathname: lastSettledPathname,
      toPathname: pathname,
      hash,
      navigationType,
      state,
    });
    lastSettledPathname = pathname;
    if (reset) window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    // After the reset, so the page's offset is read as the reader will see it.
    memory?.pageMounted(locationKey);
  }, [pathname, hash, state, navigationType, locationKey]);

  return null;
}

interface ScrollMemoryTrackerProps {
  locationKey: string;
  pathname: string;
  navigationType: RouteNavigationType;
}

/**
 * Tells the scroll memory about every location change (lib/scrollRestore.ts).
 *
 * It lives in AnimatedRoutes, which outlives the pages, and it is rendered
 * BEFORE the pathname-keyed wrapper: layout effects of earlier siblings run
 * first, so on every commit this runs ahead of RouteScrollReset. That order is
 * the point. On a drawer's marker entry (same pathname, new key) the page being
 * left must be recorded before the page-mounted call re-keys it, and on a fresh
 * mount (the public pages sit under a different provider tree) the departure
 * must be handled before the arrival marks the new page live. At that moment
 * the page being left is still on screen, so its offset is still true.
 *
 * Renders nothing.
 */
export function ScrollMemoryTracker({ locationKey, pathname, navigationType }: ScrollMemoryTrackerProps) {
  useLayoutEffect(() => {
    getScrollMemory()?.noteLocationChange({ key: locationKey, pathname, navigationType });
  }, [locationKey, pathname, navigationType]);
  return null;
}
